

import { jitsiLocalStorage } from '@jitsi/js-utils';

import { getCurrentConference, setCustomParticipantVisible } from '../base/conference';
import { TOGGLE_NAVIGATION_BAR_MORE_OPTIONS } from '../base/conference/actionTypes';
import {
    OPEN_DIALOG,
    OPEN_NAVIGATION_BAR_OVERFLOW_MENU,
    OPEN_SHEET
} from '../base/dialog/actionTypes';
import { setActiveModalId } from '../base/modal';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { conferenceViewHandler } from '../conference/actions';
import { handleWhiteboardOrEtherpadOpen } from '../conference/filmStripViewManager';
import { openEtherPadFunction, toggleDocument, toggleDocumentedit, whichParticipant } from '../etherpad/actions';
import { toggleVisibility } from '../genericiframe';
import { checkParticipant } from '../genericiframe/actions';
import { TOGGLE_SIDEBAR_MENU } from '../large-video/actionTypes';


import {
    CARMODE_MODAL_ID,
    CLEAR_TOOLBOX_TIMEOUT,
    CLOSE_CARMODE_MODAL,
    OPEN_CARMODE_MODAL,
    OPEN_WHITE_BOARD_FINAL,
    SET_FULL_SCREEN,
    SET_SHARE_MENU,
    SET_TOOLBOX_TIMEOUT,
    SET_WHITEBOARD_INSTANCE,
    TOGGLE_ETHERPAD,
    TOGGLE_OVERFLOW_MENU
} from './actionTypes';
import { actionToggleEtherPad, actionToggleWhiteBoard, openWhiteBoardFunction } from './actions.any';
import { setShareMenuVisible } from './actions.web';

// import { getLocalVideoTrack } from '../base/tracks';


declare var APP: Object;

const WHITEBOARD_COMMAND = 'WHITEBOARD_COMMAND';
const ETHERPAD_COMMIT = 'ETHERPAD_COMMIT';

/**
 * Middleware which intercepts Toolbox actions to handle changes to the
 * visibility timeout of the Toolbox.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);

    switch (action.type) {
    case OPEN_DIALOG:
    case OPEN_SHEET:
    case OPEN_NAVIGATION_BAR_OVERFLOW_MENU:
    case SET_SHARE_MENU:
    case TOGGLE_OVERFLOW_MENU:
    case TOGGLE_NAVIGATION_BAR_MORE_OPTIONS:
    case TOGGLE_SIDEBAR_MENU:
    case CLEAR_TOOLBOX_TIMEOUT: {
        const { timeoutID } = store.getState()['features/toolbox'];

        clearTimeout(timeoutID);
        break;
    }

    case SET_FULL_SCREEN: {
        const { fullScreen } = action;

        // The fixed/floating conference view is a web-only layout, and conferenceViewHandler only exists in
        // actions.web. This module is shared, so on mobile the import resolves to actions.native and the call
        // below is 'undefined is not a function'.
        if (typeof APP !== 'undefined') {
            // When entering fullscreen, switch to floating view
            // When exiting fullscreen, restore saved view preference
            const savedView = jitsiLocalStorage.getItem('conferenceView') || 'fixed';

            store.dispatch(conferenceViewHandler(fullScreen ? 'float' : savedView));
        }

        return _setFullScreen(next, action);
    }

    case SET_TOOLBOX_TIMEOUT: {
        const { timeoutID } = store.getState()['features/toolbox'];
        const { handler, timeoutMS } = action;

        clearTimeout(timeoutID);
        action.timeoutID = setTimeout(handler, timeoutMS);

        break;
    }

    case SET_WHITEBOARD_INSTANCE: {
        if (navigator.product === 'ReactNative') {
            store.dispatch(setShareMenuVisible(!getState()['features/toolbox'].visibility));
        }

        const participants = state['features/base/participants'];
        const participant = participants.filter(i => i.local === true);

        if (participants.length === 1) {

            store.dispatch(openWhiteBoardFunction(action.open));

            if (action.open) {
                store.dispatch(actionToggleWhiteBoard(true));
                store.dispatch(setCustomParticipantVisible(true));
            } else {
                conference.sendCommand(WHITEBOARD_COMMAND, {
                    attributes: {
                        value: action.open ? 'true' : 'false',
                        partId: action.open ? participant[0].id : null
                    }
                });
            }

            conference.sendCommand(WHITEBOARD_COMMAND, {
                attributes: {
                    value: action.open ? 'true' : 'false',
                    partId: action.open ? participant[0].id : null
                }
            });
        } else {
            conference.sendCommand(WHITEBOARD_COMMAND, {
                attributes: {
                    value: action.open ? 'true' : 'false',
                    partId: action.open ? participant[0].id : null
                }
            });
        }

        break;
    }
    case TOGGLE_ETHERPAD: {
        if (conference) {
            const participants = state['features/base/participants'];
            const participant = participants.filter(i => i.local === true);

            if (participants.length === 1) {
                store.dispatch(openEtherPadFunction(action.bool));

                if (action.bool) {
                    store.dispatch(actionToggleEtherPad(true));
                } else {
                    conference.sendCommand(ETHERPAD_COMMIT, {
                        attributes: {
                            value: action.bool ? 'true' : 'false',
                            partId: action.bool ? participant[0].id : null
                        }
                    });
                }

                conference.sendCommand(ETHERPAD_COMMIT, {
                    attributes: {
                        value: action.bool ? 'true' : 'false',
                        partId: action.bool ? participant[0].id : null
                    }
                });
            } else {
                conference.sendCommand(ETHERPAD_COMMIT, {
                    attributes: {
                        value: action.bool ? 'true' : 'false',
                        partId: action.bool ? participant[0].id : null
                    }
                });
            }
        }
        break;
    }
    case 'OPEN_ETHERPAD_FINAL': {
        const participants = state['features/base/participants'];
        const etherpad = state['features/etherpad'];
        const participant = participants.filter(i => i.local === true);

        conference.sendCommand(ETHERPAD_COMMIT, {
            attributes: {
                value: etherpad.openEtherPad ? 'true' : 'false',
                partId: etherpad.openEtherPad ? participant[0].id : null
            }
        });
        break;
    }

    case OPEN_WHITE_BOARD_FINAL: {
        const participants = state['features/base/participants'];
        const toolbox = state['features/toolbox'];
        const participant = participants.filter(i => i.local === true);

        conference.sendCommand(WHITEBOARD_COMMAND, {
            attributes: {
                value: toolbox.openWhiteBoard ? 'true' : 'false',
                partId: toolbox.openWhiteBoard ? participant[0].id : null
            }
        });
        break;
    }
    case OPEN_CARMODE_MODAL: {
        store.dispatch(setActiveModalId(CARMODE_MODAL_ID));
        break;
    }

    case CLOSE_CARMODE_MODAL: {
        store.dispatch(setActiveModalId());
        break;
    }
    }

    return next(action);
});

type DocumentElement = {
    +requestFullscreen ?: Function,
    +mozRequestFullScreen ?: Function,
    +webkitRequestFullscreen ?: Function
}

/**
 * Makes an external request to enter or exit full screen mode.
 *
 * @param {Dispatch} next - The redux dispatch function to dispatch the
 * specified action to the specified store.
 * @param {Action} action - The redux action SET_FULL_SCREEN which is being
 * dispatched in the specified store.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _setFullScreen(next, action) {
    if (typeof APP === 'object') {
        const { fullScreen } = action;

        if (fullScreen) {
            const documentElement: DocumentElement
                = document.documentElement || {};

            if (typeof documentElement.requestFullscreen === 'function') {
                documentElement.requestFullscreen();
            } else if (
                typeof documentElement.mozRequestFullScreen === 'function') {
                documentElement.mozRequestFullScreen();
            } else if (
                typeof documentElement.webkitRequestFullscreen === 'function') {
                documentElement.webkitRequestFullscreen();
            }
        } else {
            const fullscreenElement = document.fullscreenElement
                || document.webkitFullscreenElement
                || document.mozFullScreenElement
                || document.msFullscreenElement;

            if (typeof document.exitFullscreen === 'function') {
                // $FlowFixMe
                document.exitFullscreen();
            } else if (typeof document.mozCancelFullScreen === 'function') {
                // $FlowFixMe
                document.mozCancelFullScreen();
            } else if (typeof document.webkitExitFullscreen === 'function') {
                // $FlowFixMe
                document.webkitExitFullscreen();
            } else if (typeof document.webkitCancelFullScreen === 'function') {
                // Old WebKit API used by some mobile browsers (iOS Safari)
                // $FlowFixMe
                document.webkitCancelFullScreen();
            } else if (fullscreenElement && typeof fullscreenElement.webkitExitFullscreen === 'function') {
                // Try calling exit on the element itself (needed for some mobile browsers)
                // $FlowFixMe
                fullscreenElement.webkitExitFullscreen();
            } else if (fullscreenElement && typeof fullscreenElement.webkitCancelFullScreen === 'function') {
                // Try old WebKit API on the element itself
                // $FlowFixMe
                fullscreenElement.webkitCancelFullScreen();
            } else if (typeof document.msExitFullscreen === 'function') {
                // $FlowFixMe
                document.msExitFullscreen();
            }
        }
    }

    return next(action);
}

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear messages or close the chat modal if it's left
 * open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (conference && conference !== previousConference) {
            conference.addCommandListener(WHITEBOARD_COMMAND,
                ({ attributes }) => {
                    const { dispatch, getState } = store;

                    // const state = getState();
                    const stateMent = getState()['features/genericiframe'].visible;

                    // const _tileViewEnabled = shouldDisplayTileView(getState());
                    const { value, partId } = attributes;

                    // const toolbox = state['features/toolbox'];
                    // const participants = state['features/base/participants'];
                    // const participant = participants.filter(i => i.local === true)[0];

                    // const participantID = participants.filter(i => i.id === partId)[0];


                    // if (value === 'true') {
                    //     store.dispatch(setTileView(false));
                    // } else {
                    //     store.dispatch(setTileView(!_tileViewEnabled));
                    // }
                    // const localVideo = getLocalVideoTrack(state['features/base/tracks']);


                    store.dispatch(checkParticipant(partId));

                    const shouldBeOpen = value === 'true';

                    // Only take action if the state needs to change
                    if (shouldBeOpen && !stateMent) {
                        // Need to open
                        dispatch(toggleVisibility());
                        dispatch(actionToggleWhiteBoard(true));

                        // The filmstrip view manager drives the web layout only (it is keyed off the web
                        // 'filmStripView' preference). Calling it on mobile caused a circular dependency
                        // due to Metro's module loading order (filmStripViewManager exports were undefined),
                        // which lib-meet-hour reported as 'Error processing:WHITEBOARD_COMMAND node'.
                        // This guard ensures the function is only called on web platforms where APP is defined.
                        if (typeof APP !== 'undefined') {
                            handleWhiteboardOrEtherpadOpen(dispatch);
                        }
                    } else if (!shouldBeOpen && stateMent) {
                        // Need to close
                        dispatch(toggleVisibility());
                        dispatch(actionToggleWhiteBoard(false));
                    }

                    // If already in the correct state, do nothing
                }
            );
        }
    });

StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (conference && conference !== previousConference) {
            conference.addCommandListener(ETHERPAD_COMMIT, ({ attributes }) => {
                const { dispatch, getState } = store;
                const etherpad = getState()['features/etherpad'];
                const { editing: stateMentediting } = etherpad;

                const { value, partId } = attributes;

                // const participants = getState()['features/base/participants'];
                // const participant = participants.filter(i => i.local === true)[0];

                store.dispatch(whichParticipant(partId));

                // const localVideo = getLocalVideoTrack(getState()['features/base/tracks']);

                const shouldBeOpen = value === 'true';

                // Only take action if the state needs to change
                if (shouldBeOpen && !stateMentediting) {
                    // Need to open
                    dispatch(toggleDocument());
                    dispatch(toggleDocumentedit(true));
                    dispatch(actionToggleEtherPad(true));

                    // Web-only layout concern; see the whiteboard listener above.
                    if (typeof APP !== 'undefined') {
                        handleWhiteboardOrEtherpadOpen(dispatch);
                    }
                } else if (!shouldBeOpen && stateMentediting) {
                    // Need to close
                    dispatch(toggleDocument());
                    dispatch(toggleDocumentedit(false));
                    dispatch(actionToggleEtherPad(false));
                }

                // If already in the correct state, do nothing
            });
        }

    }
);
